import * as React from "react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

declare global {
  interface Window {
    turnstile?: {
      render: (element: HTMLElement, options: Record<string, unknown>) => string;
      reset: (widgetId?: string) => void;
    };
  }
}

const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? "";

export function TurnstileField({
  value,
  onChange,
  label = "Verification",
  resetKey,
}: {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  resetKey?: number | string;
}) {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const widgetIdRef = React.useRef<string | null>(null);
  const renderedRef = React.useRef(false);
  const observerRef = React.useRef<MutationObserver | null>(null);
  const [challengeError, setChallengeError] = React.useState(false);
  // Keep latest onChange in a ref so we don't re-run effects or recreate widget on identity changes
  const onChangeRef = React.useRef(onChange);
  React.useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // --- helpers (defined early so attemptRender can reference them) ---
  function sanitizeTurnstileAllow(container: HTMLDivElement | null) {
    if (!container) return;
    try {
      const badFeatures = ["xr-spatial-tracking", "cross-origin-isolated", "keyboard-map", "autoplay"];
      container.querySelectorAll("iframe").forEach((node) => {
        const iframe = node as HTMLIFrameElement;
        const raw = iframe.getAttribute("allow") || "";
        if (!raw) return;

        const parts = raw
          .split(/\s*;\s*/)
          .map((p) => p.trim())
          .filter(Boolean);

        const kept = parts.filter((p) => {
          const name = p.split("=")[0].trim().toLowerCase();
          return !badFeatures.includes(name);
        });

        const cleaned = kept.join("; ");
        if (cleaned !== raw) {
          iframe.setAttribute("allow", cleaned);
        }
      });
    } catch {
      // never let sanitization break anything
    }
  }

  function startAllowSanitizerObserver(container: HTMLDivElement) {
    if (observerRef.current) {
      try { observerRef.current.disconnect(); } catch {}
    }

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === "childList" && m.addedNodes.length) {
          sanitizeTurnstileAllow(container);
        }
        if (m.type === "attributes" && m.target instanceof HTMLIFrameElement && m.attributeName === "allow") {
          sanitizeTurnstileAllow(container);
        }
      }
    });

    try {
      observer.observe(container, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["allow"],
      });
      observerRef.current = observer;
    } catch {}
  }
  // --- end helpers ---

  const attemptRender = React.useCallback(() => {
    if (!window.turnstile || !ref.current || renderedRef.current) return;

    // Set early to avoid double-render races if onload + poll fire close together.
    renderedRef.current = true;

    try {
      const id = window.turnstile.render(ref.current, {
        sitekey: siteKey,
        callback: (token: string) => {
          setChallengeError(false);
          onChangeRef.current(token);
        },
        "expired-callback": () => onChangeRef.current(""),
        "error-callback": () => {
          // Cloudflare was unable to load or the user failed the challenge in a way that leaves no token.
          // Common on poor field connections; surface a calm hint instead of an unhandled throw path.
          onChangeRef.current("");
          setChallengeError(true);
        },
      });
      widgetIdRef.current = id;
      if (ref.current) {
        ref.current.dataset.rendered = "true";
        // The Turnstile iframe declares many allow="" features. Some are experimental
        // and cause very noisy "Skipping unsupported feature name" warnings in Firefox.
        // Strip the known noisy ones right after creation (and shortly after, because
        // CF populates/rewrites the iframe and its attributes asynchronously).
        sanitizeTurnstileAllow(ref.current);
        window.setTimeout(() => sanitizeTurnstileAllow(ref.current), 60);
        window.setTimeout(() => sanitizeTurnstileAllow(ref.current), 250);

        // Watch for the library replacing or mutating the iframe later (e.g. on internal retries).
        // This catches repeated attribute writes that would otherwise spam Firefox.
        startAllowSanitizerObserver(ref.current);
      }
    } catch {
      // If render itself blew up, allow a future retry.
      renderedRef.current = false;
      // Swallow render errors (e.g. postMessage origin races during failed iframe bootstrap).
      // The widget simply won't produce a token; fallback/manual entry path remains available if needed.
    }
  }, []);

  React.useEffect(() => {
    if (!siteKey || siteKey.startsWith("replace-")) return;

    const scriptId = "cloudflare-turnstile-script";
    const existing = document.getElementById(scriptId) as HTMLScriptElement | null;

    if (existing) {
      // Script tag exists (loaded or loading from a previous mount).
      if (window.turnstile) {
        attemptRender();
      } else {
        // Attach a one-time listener in case this component mounted while script was still loading.
        const onLoad = () => attemptRender();
        existing.addEventListener("load", onLoad, { once: true });
        return () => existing.removeEventListener("load", onLoad);
      }
      return;
    }

    // First mount in the app: inject the script.
    const script = document.createElement("script");
    script.id = scriptId;
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.onload = () => {
      attemptRender();
    };
    script.onerror = () => {
      // If the Cloudflare script itself fails to load (network/CSP/blocker), leave the field
      // in a state where a manual dev token can still be typed if the parent allows it.
      // We do not throw; the form will simply be missing a token until one is provided another way.
    };
    document.head.appendChild(script);

    // Safety: if the script loads extremely fast before the ref is attached, poll a few times.
    let attempts = 0;
    const poll = window.setInterval(() => {
      attempts += 1;
      if (window.turnstile && ref.current && !renderedRef.current) {
        attemptRender();
      }
      if (attempts > 20 || renderedRef.current) {
        window.clearInterval(poll);
      }
    }, 250);

    return () => window.clearInterval(poll);
  }, [attemptRender]);

  // Support explicit reset (e.g. after auth failure so user can get a fresh token).
  // reset() usually causes the library to replace its iframe, so sanitize afterward.
  React.useEffect(() => {
    if (resetKey != null && widgetIdRef.current && window.turnstile) {
      try {
        window.turnstile.reset(widgetIdRef.current);
      } catch {
        // ignore reset failures (e.g. widget already gone)
      }
      onChangeRef.current("");

      // Sanitize whatever new iframe the library created.
      const t1 = window.setTimeout(() => sanitizeTurnstileAllow(ref.current), 30);
      const t2 = window.setTimeout(() => sanitizeTurnstileAllow(ref.current), 180);
      return () => {
        window.clearTimeout(t1);
        window.clearTimeout(t2);
      };
    }
  }, [resetKey]);

  // Best-effort cleanup on unmount to avoid leaking widgets/iframes
  React.useEffect(() => {
    return () => {
      if (observerRef.current) {
        try { observerRef.current.disconnect(); } catch {}
        observerRef.current = null;
      }
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.reset(widgetIdRef.current);
        } catch {}
      }
      widgetIdRef.current = null;
      renderedRef.current = false;
    };
  }, []);

  if (siteKey && !siteKey.startsWith("replace-")) {
    return (
      <div className="grid gap-2">
        <div className="text-sm font-medium leading-none">{label}</div>
        <div ref={ref} />
        {challengeError ? (
          <p className="text-xs text-muted-foreground">
            Verification unavailable (network or browser). Check your connection, retry, or use Employee Sign In when available.
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="grid gap-2">
      <Label>{label}</Label>
      <Input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Development Turnstile token"
        autoComplete="off"
      />
    </div>
  );
}
